import { useMemo, useState, type ReactNode } from 'react';
import { Check, Globe2 } from 'lucide-react';
import { MARKETS, type Market } from '@cp/shared';
import { Flag, Input, PlatformIcon, SearchInput, Spinner } from '@/components/ui';
import type { TemplateFamilyView } from '@/types/api';

/**
 * The choices a new site asks for (ADR-0047, ADR-0059), each something to
 * pick from rather than a field to fill: a template, a market, a domain.
 */

function PickRow({ selected, onPick, children }: { readonly selected: boolean; readonly onPick: () => void; readonly children: ReactNode }): ReactNode {
  return (
    <button type="button" role="option" aria-selected={selected} className="pick-row" onClick={onPick}>
      <span className="pick-row-body">{children}</span>
      {selected ? <Check size={16} aria-hidden="true" className="pick-row-check" /> : null}
    </button>
  );
}

export function DomainPicker({
  domains,
  loading,
  value,
  onChange,
}: {
  /** Free domains the panel may point at a site; null when it may write DNS nowhere. */
  readonly domains: readonly string[] | null;
  readonly loading: boolean;
  readonly value: { readonly name: string; readonly listed: boolean } | null;
  readonly onChange: (domain: { readonly name: string; readonly listed: boolean }) => void;
}): ReactNode {
  const [query, setQuery] = useState('');
  const [typing, setTyping] = useState(false);
  const shown = (domains ?? []).filter((name) => name.includes(query.trim().toLowerCase()));

  if (loading) return <Spinner label="Загрузка доменов…" />;

  // A name given that the free list does not have — typed, or just bought — is shown typed.
  const outside = value !== null && value.name !== '' && !value.listed && !(domains ?? []).includes(value.name);

  if (!domains || domains.length === 0 || typing || outside) {
    return (
      <div className="stack stack-tight">
        <Input
          aria-label="Домен"
          placeholder="example.com"
          className="mono"
          value={value && !value.listed ? value.name : ''}
          onChange={(event) => onChange({ name: event.target.value.trim().toLowerCase(), listed: false })}
          autoComplete="off"
          spellCheck={false}
          autoFocus
        />
        <span className="field-hint">
          {domains && domains.length > 0 ? (
            <button
              type="button"
              className="link-button"
              onClick={() => {
                setTyping(false);
                onChange({ name: '', listed: false });
              }}
            >
              Выбрать из свободных доменов
            </button>
          ) : (
            'Свободных доменов в 2d2s нет — DNS для этого домена настраивается вручную'
          )}
        </span>
      </div>
    );
  }

  return (
    <div className="pick">
      {domains.length > 6 ? <SearchInput label="Найти домен" placeholder="Домен" value={query} onChange={setQuery} /> : null}
      <div className="pick-list" role="listbox" aria-label="Домен">
        {shown.map((name) => (
          <PickRow key={name} selected={value?.name === name} onPick={() => onChange({ name, listed: true })}>
            <Globe2 size={15} aria-hidden="true" className="faint" />
            <span className="mono">{name}</span>
          </PickRow>
        ))}
        {shown.length === 0 ? <p className="pick-empty">Ничего не нашлось</p> : null}
      </div>
      <button type="button" className="link-button field-hint" onClick={() => setTyping(true)}>
        Другой домен
      </button>
    </div>
  );
}

/** A template build as a tile: the picture of its reference site, its name, how many sites run it. */
export function TemplateTile({ family, selected, onPick }: { readonly family: TemplateFamilyView; readonly selected: boolean; readonly onPick: () => void }): ReactNode {
  return (
    <button type="button" role="option" aria-selected={selected} className={`pick-card${selected ? ' pick-card-selected' : ''}`} onClick={onPick}>
      <span className="pick-card-shot">
        {family.preview ? (
          <img src={`/api/v1/sites/${family.preview.siteId}/preview?t=${encodeURIComponent(family.preview.takenAt)}`} alt="" loading="lazy" />
        ) : (
          <PlatformIcon platform="wordpress" size={36} />
        )}
      </span>
      <span className="pick-card-body">
        <span className="strong truncate">{family.name}</span>
        <span className="muted text-sm">
          {family.siteCount > 0 ? `Сайтов: ${family.siteCount}` : 'Сайтов пока нет'} · версия {family.latest.version}
        </span>
      </span>
      {selected ? (
        <span className="pick-card-check" aria-hidden="true">
          <Check size={14} />
        </span>
      ) : null}
    </button>
  );
}

/**
 * The market as a grid of flags (ADR-0060): the country's flag large, its
 * name and the language under it, searched by either. What a site is for is
 * recognised at a glance rather than read.
 */
export function MarketGrid({ value, onChange }: { readonly value: string | null; readonly onChange: (market: Market) => void }): ReactNode {
  const [query, setQuery] = useState('');
  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return needle ? MARKETS.filter((item) => `${item.label} ${item.country} ${item.language} ${item.id}`.toLowerCase().includes(needle)) : MARKETS;
  }, [query]);

  return (
    <div className="stack stack-tight">
      <SearchInput label="Найти страну или язык" placeholder="Страна или язык" value={query} onChange={setQuery} />
      <div className="market-grid" role="listbox" aria-label="Страна и язык">
        {shown.map((item) => {
          const [country, language] = item.label.split(' · ');
          const selected = item.id === value;
          return (
            <button key={item.id} type="button" role="option" aria-selected={selected} className={`market-tile${selected ? ' market-tile-selected' : ''}`} onClick={() => onChange(item)}>
              <Flag country={item.country} size="xl" />
              <span className="market-tile-text">
                <span className="strong truncate">{country}</span>
                <span className="muted text-sm truncate">{language}</span>
              </span>
            </button>
          );
        })}
        {shown.length === 0 ? <p className="pick-empty">Ничего не нашлось</p> : null}
      </div>
    </div>
  );
}
